// src/api/dailyTodoApi.js
import api from "./api";

/**
 * Daily Todo API service
 * Provides methods for managing daily todo lists
 */

// ==================== SUPERADMIN OPERATIONS ====================

/**
 * Create a new daily todo list (superadmin only)
 * @param {Object} todoData - Todo list data
 * @param {string} todoData.date - Date in YYYY-MM-DD format
 * @param {string} todoData.assigned_to_id - User ID to assign to
 * @param {Array} todoData.items - Array of todo items
 * @param {string} todoData.items[].title - Item title
 * @param {string} todoData.items[].time - Item time in HH:MM:SS format (optional)
 * @param {string} todoData.items[].note - Item note/comment (optional)
 * @param {Array<string>} todoData.items[].responsible_user_ids - Responsible user IDs (optional, superadmin only)
 * @param {number} todoData.items[].order - Item order (optional)
 * @returns {Promise} Created todo list with items
 */
export const createDailyTodo = async (todoData) => {
  const response = await api.post("/daily-todos/create", todoData);
  return response.data.data;
};

/**
 * Update a daily todo list (superadmin only)
 * @param {string} todoListId - Todo list ID
 * @param {Object} updateData - Updated data
 * @param {string} updateData.date - New date in YYYY-MM-DD format (optional)
 * @returns {Promise} Updated todo list
 */
export const updateDailyTodo = async (todoListId, updateData) => {
  const response = await api.put(`/daily-todos/${todoListId}`, updateData);
  return response.data.data;
};

/**
 * Delete a daily todo list (superadmin only)
 * @param {string} todoListId - Todo list ID
 * @returns {Promise} Deletion confirmation
 */
export const deleteDailyTodo = async (todoListId) => {
  const response = await api.delete(`/daily-todos/${todoListId}`);
  return response.data;
};

/**
 * Get all daily todo lists (superadmin only)
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.page_size - Items per page
 * @param {string} params.assigned_to_id - Filter by assigned user (optional)
 * @param {string} params.date_from - Filter from date YYYY-MM-DD (optional)
 * @param {string} params.date_to - Filter to date YYYY-MM-DD (optional)
 * @returns {Promise} Paginated todo lists
 */
export const getAllDailyTodos = async (params = {}) => {
  const response = await api.get("/daily-todos/all", { params });
  return response.data.data;
};

/**
 * Add a new item to a todo list (superadmin only)
 * @param {string} todoListId - Todo list ID
 * @param {Object} itemData - Item data
 * @param {string} itemData.title - Item title
 * @param {number} itemData.order - Item order (optional)
 * @returns {Promise} Created item
 */
export const addTodoItem = async (todoListId, itemData) => {
  const formData = new FormData();
  formData.append("title", itemData.title);
  if (itemData.order !== undefined) {
    formData.append("order", itemData.order);
  }

  const response = await api.post(`/daily-todos/${todoListId}/items`, formData);
  return response.data.data;
};

/**
 * Update a todo item (superadmin only)
 * @param {string} itemId - Item ID
 * @param {Object} updateData - Updated data
 * @param {string} updateData.title - New title (optional)
 * @param {string} updateData.time - New time in HH:MM:SS format (optional)
 * @param {string} updateData.note - New note (optional)
 * @param {string} updateData.status - New status (optional)
 * @param {number} updateData.order - New order (optional)
 * @param {Array<string>} updateData.responsible_user_ids - Responsible user IDs (optional, superadmin only)
 * @returns {Promise} Updated item
 */
export const updateTodoItem = async (itemId, updateData) => {
  const response = await api.put(`/daily-todos/items/${itemId}`, updateData);
  return response.data.data;
};

/**
 * Delete a todo item (superadmin only)
 * @param {string} itemId - Item ID
 * @returns {Promise} Deletion confirmation
 */
export const deleteTodoItem = async (itemId) => {
  const response = await api.delete(`/daily-todos/items/${itemId}`);
  return response.data;
};

// ==================== USER OPERATIONS ====================

/**
 * Get current user's todo lists
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.page_size - Items per page
 * @param {string} params.date_from - Filter from date YYYY-MM-DD (optional)
 * @param {string} params.date_to - Filter to date YYYY-MM-DD (optional)
 * @param {string} params.status - Filter by status: 'all', 'completed', 'pending' (optional)
 * @returns {Promise} User's todo lists
 */
export const getMyTodoLists = async (params = {}) => {
  const response = await api.get("/daily-todos/my/lists", { params });
  return response.data.data;
};

/**
 * Get today's todo list for current user
 * @returns {Promise} Today's todo list or null
 */
export const getTodayTodos = async () => {
  const response = await api.get("/daily-todos/my/today");
  return response.data.data;
};

/**
 * Get todo list details
 * @param {string} todoListId - Todo list ID
 * @returns {Promise} Todo list details
 */
export const getTodoListDetail = async (todoListId) => {
  const response = await api.get(`/daily-todos/${todoListId}`);
  return response.data.data;
};

// ==================== ITEM STATUS MANAGEMENT ====================

/**
 * Update todo item status
 * Can be used by assigned user or superadmin
 * @param {string} itemId - Item ID
 * @param {string} status - New status: 'pending', 'in_progress', 'completed', 'cancelled'
 * @returns {Promise} Updated item
 */
export const updateTodoItemStatus = async (itemId, status) => {
  const response = await api.patch(`/daily-todos/items/${itemId}/status`, {
    status,
  });
  return response.data.data;
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Mark todo item as completed
 * @param {string} itemId - Item ID
 * @returns {Promise} Updated item
 */
export const completeTodoItem = async (itemId) => {
  return updateTodoItemStatus(itemId, "completed");
};

/**
 * Mark todo item as in progress
 * @param {string} itemId - Item ID
 * @returns {Promise} Updated item
 */
export const startTodoItem = async (itemId) => {
  return updateTodoItemStatus(itemId, "in_progress");
};

/**
 * Mark todo item as pending
 * @param {string} itemId - Item ID
 * @returns {Promise} Updated item
 */
export const resetTodoItem = async (itemId) => {
  return updateTodoItemStatus(itemId, "pending");
};

/**
 * Cancel todo item
 * @param {string} itemId - Item ID
 * @returns {Promise} Updated item
 */
export const cancelTodoItem = async (itemId) => {
  return updateTodoItemStatus(itemId, "cancelled");
};

export default {
  // Superadmin operations
  createDailyTodo,
  updateDailyTodo,
  deleteDailyTodo,
  getAllDailyTodos,
  addTodoItem,
  updateTodoItem,
  deleteTodoItem,

  // User operations
  getMyTodoLists,
  getTodayTodos,
  getTodoListDetail,

  // Status management
  updateTodoItemStatus,
  completeTodoItem,
  startTodoItem,
  resetTodoItem,
  cancelTodoItem,
};
